"use client";
import React, { FC } from "react";
import { PortableText } from "@portabletext/react";
import Button from "@/components/shared/Button";
import styles from "./Hero.module.css";

export type HeroButton = {
  text?: string;
  href?: string;
  variant?: "primary" | "secondary";
};

export type HeroTextProps = {
  title?: string;
  subtitle?: any; // Portable text array
  buttons?: HeroButton[];
  // Legacy props for backwards compatibility
  ctaText?: string;
  ctaLink?: string;
};

const defaultTitle = "Developing people and products that deliver clear results";
const defaultSubtitle = [
  {
    _type: "block",
    children: [
      {
        _type: "span",
        text: "Supporting engineering teams and companies by improving software delivery, building strong Agile systems, and guiding development and product execution. The focus is on automation, feature management, measurable outcomes, and simple delivery flow.",
      },
    ],
  },
];
const defaultButtons: HeroButton[] = [
  {
    text: "Try DevFlowOps",
    href: "https://devflowops.com",
    variant: "primary",
  },
  {
    text: "Consulting Services",
    href: "#",
    variant: "secondary",
  },
];

const HeroText: FC<HeroTextProps> = ({
  title = defaultTitle,
  subtitle = defaultSubtitle,
  buttons,
  // Legacy props
  ctaText,
  ctaLink,
}) => {
  // Handle both string (legacy) and portable text (new) formats
  const isStringSubtitle = typeof subtitle === "string";
  const portableTextValue = isStringSubtitle
    ? [
        {
          _type: "block",
          children: [
            {
              _type: "span",
              text: subtitle,
            },
          ],
        },
      ]
    : subtitle;

  // Support legacy single button or new buttons array
  const buttonsToRender =
    buttons && buttons.length > 0
      ? buttons
      : ctaText && ctaLink
        ? [{ text: ctaText, href: ctaLink, variant: "primary" as const }]
        : defaultButtons;

  return (
    <div className="mx-auto max-w-7xl pt-6 px-6 lg:px-8 text-center">
      <h1
        className={`mx-auto font-assistant font-semibold leading-tight text-4xl md:text-5xl lg:text-[70px] lg:leading-[76px] max-w-3xl mb-8 ${styles.heroTitle} ${styles.heroTitleGradient}`}
      >
        {title}
      </h1>
      <div
        className={`mx-auto font-inter font-normal text-slate-300 text-lg leading-7 max-w-3xl mb-8 ${styles.heroSubtitle}`}
      >
        {portableTextValue && Array.isArray(portableTextValue) && portableTextValue.length > 0 ? (
          <PortableText
            value={portableTextValue}
            components={{
              block: {
                normal: ({ children }) => <p>{children}</p>,
              },
              marks: {
                strong: ({ children }) => (
                  <strong className="font-semibold">{children}</strong>
                ),
                em: ({ children }) => (
                  <em className="italic">{children}</em>
                ),
                highlight: ({ children }) => (
                  <mark className="bg-[#7f56d9] text-white px-1.5 py-0.5 rounded font-semibold">
                    {children}
                  </mark>
                ),
                link: ({ value, children }) => {
                  const href = value?.href;
                  let shouldOpenInNewWindow = Boolean(value?.blank);

                  if (!shouldOpenInNewWindow && href) {
                    try {
                      const parsed = new URL(href);
                      shouldOpenInNewWindow =
                        parsed.protocol === "http:" || parsed.protocol === "https:";
                    } catch {
                      // Ignore invalid/relative URLs and preserve default behavior.
                    }
                  }

                  const target = shouldOpenInNewWindow ? "_blank" : undefined;
                  const rel = shouldOpenInNewWindow
                    ? "noopener noreferrer"
                    : undefined;
                  return (
                    <a
                      href={href}
                      target={target}
                      rel={rel}
                      className="text-[#a78bfa] hover:text-[#c4b5fd] underline decoration-[#a78bfa]/50 hover:decoration-[#a78bfa] transition-colors duration-200 ease-in-out font-medium"
                    >
                      {children}
                    </a>
                  );
                },
              },
            }}
          />
        ) : (
          <p>{isStringSubtitle ? subtitle : ""}</p>
        )}
      </div>
      <nav
        className={`flex flex-wrap justify-center items-start gap-[15px] relative pb-20 ${styles.heroButton}`}
        role="navigation"
        aria-label="Call to action buttons"
      >
        {buttonsToRender.map((button, index) => (
          <Button
            key={index}
            variant={button.variant || "primary"}
            href={button.href}
            ariaLabel={button.text}
          >
            {button.text}
          </Button>
        ))}
      </nav>
    </div>
  );
};

export default HeroText;
